import { supabase } from './supabase';
import type { BankTransaction, ApiResponse } from '../types/database';

export type ExpectedActionType = 'recurring' | 'known';

// ── Match History types ────────────────────────────────────────────────────────

/**
 * A fully-hydrated matched GROUP: one or more bank transactions + one or more
 * cashflow-side actions linked by the same reconciliation_group_id.
 */
export interface MatchedGroup {
  /** Shared group UUID (= reconciliation_group_id on every row) */
  groupId: string;
  /** All bank transactions in this group (1 or more) */
  bankTxs: BankTransaction[];
  /** All cashflow actions in this group (1 or more) */
  actions: ExpectedAction[];
}

/**
 * @deprecated Use MatchedGroup for new code.
 * Legacy type kept for backward-compat with MatchHistoryView until migrated.
 */
export interface MatchedPair {
  bankTx: BankTransaction;
  action: ExpectedAction;
}

export interface ExpectedAction {
  id: string;
  type: ExpectedActionType;
  action_date: string;
  description: string;
  amount: number;
  bank_account_id: string;
  accounting_category_id: string;
  status: string;
  matched_bank_transaction_id?: string | null;
  /** Shared group UUID – present after M:N matching */
  reconciliation_group_id?: string | null;
  bank_accounts?: {
    nickname: string;
  };
  accounting_categories?: {
    name: string;
    cashflow_mapping: string;
  };
  offset_codes?: {
    name: string;
  };
}

/**
 * Service for managing bank reconciliation and matching
 */
class ReconciliationService {
  /**
   * Get unmatched bank transactions
   */
  async getUnmatchedBankTransactions(companyId: string): Promise<ApiResponse<BankTransaction[]>> {
    try {
      const { data, error } = await supabase
        .from('bank_transactions')
        .select(`
          *,
          bank_accounts (
            nickname
          ),
          accounting_categories (
            name,
            cashflow_mapping
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .order('transaction_date', { ascending: false });

      if (error) throw error;

      return {
        data: data as BankTransaction[],
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Get unmatched bank transactions error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to fetch unmatched transactions',
        },
        status: 500,
      };
    }
  }

  /**
   * Get pending expected actions (both recurring and known)
   */
  async getPendingExpectedActions(companyId: string): Promise<ApiResponse<ExpectedAction[]>> {
    try {
      const { data: recurringData, error: recurringError } = await supabase
        .from('recurring_actions_lines')
        .select(`
          *,
          bank_accounts (
            nickname
          ),
          accounting_categories (
            name,
            cashflow_mapping
          ),
          offset_codes (
            name
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .order('action_date', { ascending: false });

      if (recurringError) throw recurringError;

      const { data: knownData, error: knownError } = await supabase
        .from('known_actions')
        .select(`
          *,
          bank_accounts (
            nickname
          ),
          accounting_categories (
            name,
            cashflow_mapping
          ),
          offset_codes (
            name
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .order('action_date', { ascending: false });

      if (knownError) throw knownError;

      const recurringActions: ExpectedAction[] = (recurringData || []).map((action: any) => ({
        ...action,
        type: 'recurring' as ExpectedActionType,
      }));

      const knownActions: ExpectedAction[] = (knownData || []).map((action: any) => ({
        ...action,
        type: 'known' as ExpectedActionType,
      }));

      const allActions = [...recurringActions, ...knownActions].sort((a, b) => {
        return new Date(b.action_date).getTime() - new Date(a.action_date).getTime();
      });

      return {
        data: allActions,
        error: null,
        status: 200,
      };
    } catch (error) {
      console.error('Get pending expected actions error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to fetch expected actions',
        },
        status: 500,
      };
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MANY-TO-MANY MATCH
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Match N bank transactions to M expected actions.
   *
   * All supplied rows are stamped with the same freshly-generated
   * `reconciliation_group_id` UUID and their `status` is set to 'matched'.
   *
   * For backward-compatibility, when exactly one bank tx is matched to exactly
   * one expected action the legacy `matched_bank_transaction_id` column on the
   * action row is also populated.
   *
   * Pre-condition (enforced in the UI before calling this):
   *   abs(sum(bankTx.amounts)) ≈ abs(sum(expectedAction.amounts))
   */
  async matchManyToMany(
    bankTransactionIds: string[],
    expectedActions: ExpectedAction[],
  ): Promise<ApiResponse<{ groupId: string }>> {
    if (bankTransactionIds.length === 0 || expectedActions.length === 0) {
      return {
        data: null,
        error: { message: 'חייב לבחור לפחות תנועת בנק אחת ופעולה צפויה אחת' },
        status: 400,
      };
    }

    // Generate a single UUID that will tie all rows together.
    const groupId = crypto.randomUUID();

    try {
      // ── 1. Bank transactions → matched ──────────────────────────────────
      const { error: bankErr } = await supabase
        .from('bank_transactions')
        .update({
          status: 'matched',
          reconciliation_group_id: groupId,
        })
        .in('id', bankTransactionIds);

      if (bankErr) throw bankErr;

      // ── 2. Expected actions → matched ────────────────────────────────────
      // Split by type (recurring vs known) since they live in different tables.
      const recurringActions = expectedActions.filter((a) => a.type === 'recurring');
      const knownActions     = expectedActions.filter((a) => a.type === 'known');

      // For single 1:1 matches keep backward-compat legacy field populated.
      const legacyBankTxId =
        bankTransactionIds.length === 1 && expectedActions.length === 1
          ? bankTransactionIds[0]
          : null;

      if (recurringActions.length > 0) {
        const { error: recErr } = await supabase
          .from('recurring_actions_lines')
          .update({
            status: 'matched',
            reconciliation_group_id: groupId,
            matched_bank_transaction_id: legacyBankTxId,
          })
          .in('id', recurringActions.map((a) => a.id));

        if (recErr) throw recErr;
      }

      if (knownActions.length > 0) {
        const { error: knownErr } = await supabase
          .from('known_actions')
          .update({
            status: 'matched',
            reconciliation_group_id: groupId,
            matched_bank_transaction_id: legacyBankTxId,
          })
          .in('id', knownActions.map((a) => a.id));

        if (knownErr) throw knownErr;
      }

      return { data: { groupId }, error: null, status: 200 };
    } catch (error) {
      console.error('matchManyToMany error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to match transactions',
        },
        status: 500,
      };
    }
  }

  /**
   * Legacy 1:1 match — kept for use by the batch-approve suggestions flow
   * where each suggestion is already a guaranteed 1:1 pair.
   */
  async matchTransactions(
    bankTransactionId: string,
    expectedAction: ExpectedAction,
  ): Promise<ApiResponse<void>> {
    const { error } = await this.matchManyToMany(
      [bankTransactionId],
      [expectedAction],
    );
    if (error) return { data: null, error, status: 500 };
    return { data: null, error: null, status: 200 };
  }

  // ────────────────────────────────────────────────────────────────────────────
  // UNDO — by group ID
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Undo an entire reconciliation group.
   *
   * Resets every bank transaction and every action row in the group back to
   * status='pending' and clears reconciliation_group_id (and the legacy
   * matched_bank_transaction_id field).
   */
  async undoGroup(groupId: string): Promise<ApiResponse<void>> {
    try {
      // Reset bank transactions
      const { error: bankErr } = await supabase
        .from('bank_transactions')
        .update({ status: 'pending', reconciliation_group_id: null })
        .eq('reconciliation_group_id', groupId);

      if (bankErr) throw bankErr;

      // Reset recurring action lines
      const { error: recErr } = await supabase
        .from('recurring_actions_lines')
        .update({
          status: 'pending',
          reconciliation_group_id: null,
          matched_bank_transaction_id: null,
        })
        .eq('reconciliation_group_id', groupId);

      if (recErr) throw recErr;

      // Reset known actions
      const { error: knownErr } = await supabase
        .from('known_actions')
        .update({
          status: 'pending',
          reconciliation_group_id: null,
          matched_bank_transaction_id: null,
        })
        .eq('reconciliation_group_id', groupId);

      if (knownErr) throw knownErr;

      return { data: null, error: null, status: 200 };
    } catch (err) {
      console.error('undoGroup error:', err);
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to undo match group' },
        status: 500,
      };
    }
  }

  /**
   * Legacy single-row undo (kept for MatchHistoryView backward-compat).
   * Delegates to undoGroup when the row has a group_id, otherwise falls back
   * to the original row-level reset.
   */
  async unmatchTransaction(bankTransactionId: string): Promise<ApiResponse<void>> {
    try {
      // Fetch the bank tx to see if it has a group_id
      const { data: tx } = await supabase
        .from('bank_transactions')
        .select('reconciliation_group_id')
        .eq('id', bankTransactionId)
        .single();

      if (tx?.reconciliation_group_id) {
        return this.undoGroup(tx.reconciliation_group_id);
      }

      // Fallback: original row-level reset (legacy rows without group_id)
      const { data: recurringData } = await supabase
        .from('recurring_actions_lines')
        .select('id')
        .eq('matched_bank_transaction_id', bankTransactionId)
        .single();

      if (recurringData) {
        const { error: recErr } = await supabase
          .from('recurring_actions_lines')
          .update({ status: 'pending', matched_bank_transaction_id: null })
          .eq('id', recurringData.id);
        if (recErr) throw recErr;
      }

      const { data: knownData } = await supabase
        .from('known_actions')
        .select('id')
        .eq('matched_bank_transaction_id', bankTransactionId)
        .single();

      if (knownData) {
        const { error: knownErr } = await supabase
          .from('known_actions')
          .update({ status: 'pending', matched_bank_transaction_id: null })
          .eq('id', knownData.id);
        if (knownErr) throw knownErr;
      }

      const { error: bankErr } = await supabase
        .from('bank_transactions')
        .update({ status: 'pending' })
        .eq('id', bankTransactionId);

      if (bankErr) throw bankErr;

      return { data: null, error: null, status: 200 };
    } catch (error) {
      console.error('unmatchTransaction error:', error);
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : 'Failed to unmatch transaction',
        },
        status: 500,
      };
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // MATCH HISTORY
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Get all matched groups for a company.
   *
   * Returns MatchedGroup[] where each group may contain multiple bank
   * transactions and/or multiple expected actions.
   *
   * Legacy rows (no reconciliation_group_id) are each wrapped in their own
   * synthetic single-element group keyed by the bank tx id so the history view
   * still renders them correctly.
   */
  async getMatchedTransactions(companyId: string): Promise<ApiResponse<MatchedGroup[]>> {
    try {
      // ── 1. All matched bank transactions ──────────────────────────────────
      const { data: bankRows, error: bankErr } = await supabase
        .from('bank_transactions')
        .select(`
          *,
          bank_accounts ( nickname ),
          accounting_categories ( name, cashflow_mapping )
        `)
        .eq('company_id', companyId)
        .eq('status', 'matched')
        .order('transaction_date', { ascending: false });

      if (bankErr) throw bankErr;
      if (!bankRows || bankRows.length === 0) {
        return { data: [], error: null, status: 200 };
      }

      // ── 2. Matched recurring lines ─────────────────────────────────────
      const { data: recurringRows, error: recErr } = await supabase
        .from('recurring_actions_lines')
        .select(`
          *,
          bank_accounts ( nickname ),
          accounting_categories ( name, cashflow_mapping ),
          offset_codes ( name )
        `)
        .eq('company_id', companyId)
        .eq('status', 'matched');

      if (recErr) throw recErr;

      // ── 3. Matched known actions ──────────────────────────────────────
      const { data: knownRows, error: knownErr } = await supabase
        .from('known_actions')
        .select(`
          *,
          bank_accounts ( nickname ),
          accounting_categories ( name, cashflow_mapping ),
          offset_codes ( name )
        `)
        .eq('company_id', companyId)
        .eq('status', 'matched');

      if (knownErr) throw knownErr;

      // ── 4. Build group maps ─────────────────────────────────────────────
      // group_id → ExpectedAction[]
      const actionsByGroup = new Map<string, ExpectedAction[]>();
      // bankTxId → ExpectedAction (legacy fallback)
      const actionByBankId = new Map<string, ExpectedAction>();

      const collectActions = (rows: any[], type: ExpectedActionType) => {
        for (const row of rows || []) {
          const action: ExpectedAction = { ...(row as any), type };
          const gid = row.reconciliation_group_id as string | null;
          if (gid) {
            if (!actionsByGroup.has(gid)) actionsByGroup.set(gid, []);
            actionsByGroup.get(gid)!.push(action);
          } else if (row.matched_bank_transaction_id) {
            // Legacy 1:1 row — key by bank tx id
            actionByBankId.set(row.matched_bank_transaction_id as string, action);
          }
        }
      };

      collectActions(recurringRows || [], 'recurring');
      collectActions(knownRows    || [], 'known');

      // ── 5. Build groups from bank-tx side ──────────────────────────────
      // group_id → BankTransaction[]
      const banksByGroup = new Map<string, BankTransaction[]>();
      // legacy rows without group_id
      const legacyBankTxs: BankTransaction[] = [];

      for (const row of bankRows as BankTransaction[]) {
        const gid = row.reconciliation_group_id;
        if (gid) {
          if (!banksByGroup.has(gid)) banksByGroup.set(gid, []);
          banksByGroup.get(gid)!.push(row);
        } else {
          legacyBankTxs.push(row);
        }
      }

      // ── 6. Assemble MatchedGroup[] ──────────────────────────────────────
      const groups: MatchedGroup[] = [];

      // Groups with a real group_id
      for (const [gid, bTxs] of banksByGroup.entries()) {
        const acts = actionsByGroup.get(gid) ?? [];
        groups.push({ groupId: gid, bankTxs: bTxs, actions: acts });
      }

      // Legacy 1:1 rows (no group_id) — each becomes its own synthetic group
      for (const bTx of legacyBankTxs) {
        const act = actionByBankId.get(bTx.id);
        if (act) {
          groups.push({
            groupId: bTx.id,         // synthetic key
            bankTxs: [bTx],
            actions: [act],
          });
        }
      }

      // Sort: newest first by the most-recent bank-tx date in the group
      groups.sort((a, b) => {
        const latestA = a.bankTxs.reduce(
          (acc, t) => (t.transaction_date > acc ? t.transaction_date : acc),
          '',
        );
        const latestB = b.bankTxs.reduce(
          (acc, t) => (t.transaction_date > acc ? t.transaction_date : acc),
          '',
        );
        return latestB.localeCompare(latestA);
      });

      return { data: groups, error: null, status: 200 };
    } catch (err) {
      console.error('getMatchedTransactions error:', err);
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch match history' },
        status: 500,
      };
    }
  }

  /**
   * Undo a specific match (targeted by bankTxId + actionId + actionType).
   *
   * Kept for backward-compat with MatchHistoryView.
   * If the row has a reconciliation_group_id the ENTIRE group is undone.
   */
  async undoMatch(
    bankTxId: string,
    targetActionId: string,
    targetType: ExpectedActionType,
  ): Promise<ApiResponse<void>> {
    try {
      // Prefer group-based undo when the bank tx has a group_id
      const { data: tx } = await supabase
        .from('bank_transactions')
        .select('reconciliation_group_id')
        .eq('id', bankTxId)
        .single();

      if (tx?.reconciliation_group_id) {
        return this.undoGroup(tx.reconciliation_group_id);
      }

      // Legacy single-row undo
      const tableName =
        targetType === 'recurring' ? 'recurring_actions_lines' : 'known_actions';

      const { error: actionErr } = await supabase
        .from(tableName)
        .update({ status: 'pending', matched_bank_transaction_id: null })
        .eq('id', targetActionId);

      if (actionErr) throw actionErr;

      const { error: bankErr } = await supabase
        .from('bank_transactions')
        .update({ status: 'pending' })
        .eq('id', bankTxId);

      if (bankErr) throw bankErr;

      return { data: null, error: null, status: 200 };
    } catch (err) {
      console.error('undoMatch error:', err);
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to undo match' },
        status: 500,
      };
    }
  }
}

export const reconciliationService = new ReconciliationService();
